'use client'

import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react'

// ── Types ─────────────────────────────────────────────────────────────────────

interface BreadcrumbContextValue {
  /** Register a human-readable label for a URL segment value (e.g. a UUID or slug). */
  setOverride: (segmentValue: string, label: string) => void
  /** Look up a registered label; returns null if not registered. */
  getOverride: (segmentValue: string) => string | null
}

// ── Context ───────────────────────────────────────────────────────────────────

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  setOverride: () => undefined,
  getOverride: () => null,
})

// ── Provider ──────────────────────────────────────────────────────────────────

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  // Use a ref (not state) so updates never trigger a re-render of the provider tree.
  // The Header reads from this on every render via its own hook call.
  const overrides = useRef<Map<string, string>>(new Map())

  const setOverride = useCallback((segmentValue: string, label: string) => {
    overrides.current.set(segmentValue, label)
  }, [])

  const getOverride = useCallback((segmentValue: string): string | null => {
    return overrides.current.get(segmentValue) ?? null
  }, [])

  return (
    <BreadcrumbContext.Provider value={{ setOverride, getOverride }}>
      {children}
    </BreadcrumbContext.Provider>
  )
}

// ── Consumer hook (used by Header) ────────────────────────────────────────────

export function useBreadcrumb() {
  return useContext(BreadcrumbContext)
}

// ── Page-level hook (used by detail pages) ────────────────────────────────────

/**
 * Call this inside a detail page after the entity name has been loaded.
 * The segment value is the raw URL segment (UUID / slug).
 * The label is the human-readable name to display in the breadcrumb.
 *
 * @example
 *   useBreadcrumbOverride(sampleId, sample.sample_name)
 */
export function useBreadcrumbOverride(segmentValue: string | undefined, label: string | undefined) {
  const { setOverride } = useBreadcrumb()

  useEffect(() => {
    if (segmentValue && label) {
      setOverride(segmentValue, label)
    }
  }, [segmentValue, label, setOverride])
}
